import React, { useEffect, useState, useCallback } from 'react';
import ProtectedAdminRoute from '../../components/ProtectedAdminRoute';
import '../../styles/admin/AdminMessages.css';
import { useSecureAdminAuth } from '../../auth/useSecureAdminAuth';
import DOMPurify from 'dompurify';

import { apiFetch } from '../../utils/api';

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Get admin authentication context
  const { adminUser, isAdminLoading } = useSecureAdminAuth();

  // Fetch messages function that can be reused
  const fetchMessages = useCallback(async () => {
    // Ensure adminUser and a token are available before proceeding
    if (isAdminLoading || !adminUser || !adminUser.accessToken) {
        setLoading(false); // Make sure loading state is off if condition not met
        return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log("Fetching messages with admin authentication...");
      
      // Get current auth token using the authenticated admin user
      // FIX: Remove await from adminUser.accessToken
      const idToken = adminUser.accessToken;

      // Build query parameters for filtering and pagination
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20); // Show 20 messages per page
      
      // Only add status filter if not "all"
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      const queryString = params.toString();
      
      // Fetch messages with authentication header using enhanced API
      const data = await apiFetch(`/api/contact-new?${queryString}`, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });
      console.log("Messages response:", data);
      // Check if the request was successful
      if (data.success) {
        setMessages(data.contacts || []);
        // Set pagination information
        if (data.pagination) {
          setTotalPages(data.pagination.pages || 1);
        }
        if (data.contacts && data.contacts.length === 0) {
          console.log("No messages found");
        }
      } else {
        throw new Error(data.message || "Failed to fetch messages");
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setError(err.message || "Failed to load messages. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [adminUser, isAdminLoading, page, statusFilter]); // Re-added page and statusFilter to deps

  // Fetch messages when admin authentication is ready or filters/pagination changes
  useEffect(() => {
    // Only proceed if isAdminLoading is false AND adminUser is present
    if (!isAdminLoading && adminUser) {
      fetchMessages();
    }
  }, [adminUser, isAdminLoading, fetchMessages, page, statusFilter]); // Depend on page and statusFilter

  // Mark as handled - updated for enhanced API
  const handleMarkHandled = async (id) => {
    try {
      setError(null);
      
      // Use the admin user from useSecureAdminAuth
      // Ensure adminUser and accessToken are available
      if (!adminUser || !adminUser.accessToken) {
        setError("Authentication required or token unavailable. Please log in again.");
        console.error("Authentication failed: adminUser or accessToken missing for handleMarkHandled.");
        return;
      }
      
      console.log(`Marking message ${id} as handled...`);
      
      // Get ID token from the admin user
      // FIX: Remove await from adminUser.accessToken
      const idToken = adminUser.accessToken;

      // Send request with authentication header - using new enhanced API
      const data = await apiFetch(`/api/contact-new/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'responded' })
      });
      console.log("Mark handled response:", data);
      if (data.success) {
        console.log(`Successfully marked message ${id} as responded`);
        setMessages(msgs =>
          msgs.map(m => m._id === id ? { ...m, status: 'responded' } : m)
        );
      } else {
        throw new Error(data.message || "Failed to mark message as handled");
      }
    } catch (err) {
      console.error("Failed to mark as handled:", err);
      setError(err.message || "Failed to mark as handled. Please try again.");
    }
  };

  // Add or update admin notes
  const [noteText, setNoteText] = useState('');
  const [editingNotesId, setEditingNotesId] = useState(null);

  const handleSaveNotes = async (id) => {
    try {
      setError(null);
      
      if (!adminUser || !adminUser.accessToken) {
        setError("Authentication required or token unavailable. Please log in again.");
        console.error("Authentication failed: adminUser or accessToken missing for handleSaveNotes.");
        return;
      }
      
      // Get ID token from the admin user
      // FIX: Remove await from adminUser.accessToken
      const idToken = adminUser.accessToken;

      // Send request with authentication header
      const data = await apiFetch(`/api/contact-new/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ adminNotes: noteText })
      });
      if (data.success) {
        // Update messages state with new notes
        setMessages(msgs =>
          msgs.map(m => m._id === id ? { ...m, adminNotes: data.contact.adminNotes } : m)
        );
        // Reset notes editing state
        setEditingNotesId(null);
        setNoteText('');
      } else {
        throw new Error(data.message || "Failed to save notes");
      }
    } catch (err) {
      console.error("Failed to save notes:", err);
      setError(err.message || "Failed to save notes. Please try again.");
    }
  };

  // Secure HTML sanitization using DOMPurify
  const sanitize = (text) => {
    if (!text) return '';
    return DOMPurify.sanitize(text, { 
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
      ALLOWED_ATTR: []
    });
  };
  
  // Calculate stats about decryption success/failure
  const decryptionStats = React.useMemo(() => {
    if (!messages.length) return null;
    
    const total = messages.length;
    const decryptionErrors = messages.filter(
      msg => msg.decryptionError || 
                  msg.name === '[Decryption Error]' || 
                  msg.email === '[Decryption Error]' || 
                  msg.message === '[Decryption Error]'
    ).length;
    
    // Status statistics
    const newMessages = messages.filter(msg => msg.status === 'new').length;
    const readMessages = messages.filter(msg => msg.status === 'read').length;
    const respondedMessages = messages.filter(msg => msg.status === 'responded').length;
    const archivedMessages = messages.filter(msg => msg.status === 'archived').length;
    
    return {
      total,
      decryptionErrors,
      successRate: `${Math.round(((total - decryptionErrors) / total) * 100)}%`,
      statusStats: {
        new: newMessages,
        read: readMessages,
        responded: respondedMessages,
        archived: archivedMessages
      }
    };
  }, [messages]);

  return (
    <ProtectedAdminRoute>
      <div className="admin-messages-container">
        <div className="admin-messages-header" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Contact Messages</h2>
            <button 
              onClick={fetchMessages} 
              disabled={loading || isAdminLoading} 
              style={{ padding: '8px 16px', cursor: loading || isAdminLoading ? 'not-allowed' : 'pointer' }}
            >
              {loading ? 'Refreshing...' : 'Refresh Messages'}
            </button>
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <div style={{ marginRight: '15px' }}>
              <label htmlFor="statusFilter" style={{ marginRight: '8px', fontWeight: 'bold' }}>Status:</label>
              <select 
                id="statusFilter" 
                value={statusFilter} 
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} 
                style={{ padding: '6px', borderRadius: '4px' }}
                disabled={loading}
              >
                <option value="all">All Messages</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="responded">Responded</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            
            {decryptionStats?.statusStats && (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '0.9rem' }}>
                <span style={{ backgroundColor: '#e9f5ff', padding: '2px 8px', borderRadius: '15px', border: '1px solid #cce5ff' }}>
                  New: {decryptionStats.statusStats.new}
                </span>
                <span style={{ backgroundColor: '#f8f9fa', padding: '2px 8px', borderRadius: '15px', border: '1px solid #dee2e6' }}>
                  Read: {decryptionStats.statusStats.read}
                </span>
                <span style={{ backgroundColor: '#e8f5e9', padding: '2px 8px', borderRadius: '15px', border: '1px solid #c8e6c9' }}>
                  Responded: {decryptionStats.statusStats.responded}
                </span>
                <span style={{ backgroundColor: '#f8f9fa', padding: '2px 8px', borderRadius: '15px', border: '1px solid #dee2e6' }}>
                  Archived: {decryptionStats.statusStats.archived}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Show error message if there's an error */}
        {error && (
          <div className="error-message" style={{ color: 'red', margin: '10px 0', padding: '10px', backgroundColor: '#ffecec', border: '1px solid #f5aca6', borderRadius: '5px' }}>
            <strong>Error:</strong> {error}
            <div style={{ marginTop: '10px' }}>
              <button onClick={fetchMessages} style={{ padding: '5px 10px' }}>Try Again</button>
            </div>
          </div>
        )}
        
        {/* Loading indicator */}
        {loading ? (
          <div className="loading-indicator" style={{ textAlign: 'center', margin: '20px 0', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <div style={{ marginBottom: '10px' }}>Loading messages...</div>
            <div className="spinner" style={{ 
              width: '30px', 
              height: '30px', 
              margin: '0 auto',
              border: '3px solid rgba(0,0,0,0.1)', 
              borderRadius: '50%', 
              borderTopColor: '#3498db', 
              animation: 'spin 1s linear infinite' 
            }}></div>
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
          </div>
        ) : messages.length === 0 ? (
          <div className="no-messages" style={{ textAlign: 'center', margin: '20px 0', padding: '40px 20px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
            <p>No contact messages found.</p>
            <button onClick={fetchMessages} style={{ marginTop: '10px', padding: '8px 16px' }}>Refresh</button>
          </div>
        ) : (
          <>
            {/* Decryption status bar */}
            {decryptionStats && (
              <div className="decryption-stats" style={{
                margin: '10px 0 20px',
                padding: '10px 15px',
                backgroundColor: decryptionStats.decryptionErrors > 0 ? '#fff3cd' : '#d4edda',
                borderRadius: '5px',
                border: `1px solid ${decryptionStats.decryptionErrors > 0 ? '#ffeeba' : '#c3e6cb'}`,
                color: decryptionStats.decryptionErrors > 0 ? '#856404' : '#155724',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>Decryption Status:</strong> {decryptionStats.successRate} successful 
                  ({decryptionStats.total - decryptionStats.decryptionErrors} of {decryptionStats.total} messages)
                </div>
                {decryptionStats.decryptionErrors > 0 && (
                  <div>
                    <span role="img" aria-label="warning" style={{ marginRight: '5px' }}>⚠️</span>
                    <strong>{decryptionStats.decryptionErrors} message{decryptionStats.decryptionErrors > 1 ? 's' : ''}</strong> failed to decrypt
                  </div>
                )}
              </div>
            )}
            
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map(msg => (
                  <React.Fragment key={msg._id}>
                    <tr>
                      {/* Handle possible decryption errors */}
                      <td>{msg.name === '[Decryption Error]' ? <span style={{ color: 'red' }}>Decryption Error</span> : msg.name}</td>
                      <td>{msg.email === '[Decryption Error]' ? <span style={{ color: 'red' }}>Decryption Error</span> : msg.email}</td>
                      <td>{msg.subject}</td>
                      <td
                        style={{
                          maxWidth: 200,
                          whiteWhiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer'
                        }}
                        onClick={() => setExpandedId(expandedId === msg._id ? null : msg._id)}
                        title="Click to toggle full message"
                      >
                        {msg.message === '[Decryption Error]' ? 
                          <span style={{ color: 'red' }}>Decryption Error</span> :
                          msg.message.length > 40 ?
                            `${msg.message.slice(0, 40)}...` :
                            msg.message
                        }
                      </td>
                      <td>{new Date(msg.createdAt).toLocaleDateString()}</td>
                      <td style={{ textAlign: 'center' }}>
                        {msg.status === 'new' && <span style={{ color: '#007bff' }}>New</span>}
                        {msg.status === 'read' && <span style={{ color: '#6c757d' }}>Read</span>}
                        {msg.status === 'responded' && <span style={{ color: '#28a745' }}>✅ Responded</span>}
                        {msg.status === 'archived' && <span style={{ color: '#6c757d' }}>Archived</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {msg.status === 'new' || msg.status === 'read' ? (
                          <button onClick={() => handleMarkHandled(msg._id)}>
                            Mark as responded
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {expandedId === msg._id && (
                      <tr className="full-message-row">
                        <td colSpan={7}>
                          <strong>Full message:</strong>
                          {msg.message === '[Decryption Error]' ? 
                            <div className="error-message" style={{ color: 'red' }}>
                              Unable to decrypt this message. The encryption key may have changed.
                            </div> :
                            <div
                              className="full-message-content"
                              dangerouslySetInnerHTML={{ __html: sanitize(msg.message) }}
                            ></div>
                          }
                          <div className="message-metadata">
                            <strong>Received:</strong> {new Date(msg.createdAt).toLocaleString()}
                          </div>
                          
                          {/* Admin notes section */}
                          <div className="admin-notes" style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                            <h4 style={{ marginBottom: '10px' }}>Admin Notes</h4>
                            
                            {editingNotesId === msg._id ? (
                              <div style={{ marginBottom: '10px' }}>
                                <textarea
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  style={{ 
                                    width: '100%', 
                                    padding: '8px', 
                                    marginBottom: '10px',
                                    borderRadius: '4px',
                                    border: '1px solid #ced4da',
                                    minHeight: '80px'
                                  }}
                                  placeholder="Add your notes here..."
                                ></textarea>
                                
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <button 
                                    onClick={() => handleSaveNotes(msg._id)}
                                    style={{ padding: '5px 10px' }}
                                  >
                                    Save Notes
                                  </button>
                                  <button 
                                    onClick={() => {
                                      setEditingNotesId(null);
                                      setNoteText('');
                                    }}
                                    style={{ padding: '5px 10px' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div>
                                {msg.adminNotes ? (
                                  <div style={{ 
                                    backgroundColor: '#f8f9fa', 
                                    padding: '10px', 
                                    borderRadius: '4px', 
                                    marginBottom: '10px',
                                    border: '1px solid #dee2e6'
                                  }}>
                                    {msg.adminNotes}
                                  </div>
                                ) : (
                                  <div style={{ color: '#6c757d', marginBottom: '10px' }}>No notes added yet.</div>
                                )}
                                
                                <button 
                                  onClick={() => {
                                    setEditingNotesId(msg._id);
                                    setNoteText(msg.adminNotes || '');
                                  }}
                                  style={{ padding: '5px 10px' }}
                                >
                                  {msg.adminNotes ? 'Edit Notes' : 'Add Notes'}
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            
            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="pagination-controls" style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                margin: '20px 0',
                gap: '10px'
              }}>
                <button 
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                  style={{ 
                    padding: '5px 10px',
                    cursor: page === 1 || loading ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.6 : 1
                  }}
                >
                  &laquo; Previous
                </button>
                
                <span style={{ margin: '0 10px' }}>
                  Page {page} of {totalPages}
                </span>
                
                <button 
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                  style={{ 
                    padding: '5px 10px',
                    cursor: page === totalPages || loading ? 'not-allowed' : 'pointer',
                    opacity: page === totalPages ? 0.6 : 1
                  }}
                >
                  Next &raquo;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </ProtectedAdminRoute>
  );
}